'use client';

import { Box, Link, Typography } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import React, { FC, useEffect, useState } from 'react';

import { GET_LEASE_SUBTENANT, VERIFY_SEQUENCING_TIERS_ENABLED } from '../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../types';
import { isAdminDashboard, isTenantPortal } from '../../utils';
import { LeasingRightsTable } from './leasingRightsTable';
import { Sequencing } from './sequencing/sequencing';

type SubscriptionLeasesProps = {
	subscriptionId: string;
	accountId: string | number;
	facilityId: string;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
	getUrlToSubscriptionPage?: (...x: any[]) => string;
};

export const SubscriptionLeases: FC<SubscriptionLeasesProps> = ({
	subscriptionId,
	accountId,
	facilityId,
	setShowDialog,
	setShowSnackbar,
	getUrlToSubscriptionPage,
}) => {
	const [showSequencing, setShowSequencing] = useState<boolean>(false);

	const { data: sequencingData } = useQuery(VERIFY_SEQUENCING_TIERS_ENABLED, {
		variables: { subscriptionId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !subscriptionId,
	});

	const { data } = useQuery(GET_LEASE_SUBTENANT, {
		variables: { subscriptionId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !subscriptionId,
	});

	const leaseSubtenant = data?.getLeaseSubtenant;
	const subscriptionSubtenantType = leaseSubtenant?.type;
	const parentSubscription = leaseSubtenant?.parentSubscription;

	useEffect(() => {
		if (sequencingData?.verifySequencingTiersEnabled) {
			setShowSequencing(sequencingData.verifySequencingTiersEnabled.showSequencing);
		}
	}, [sequencingData, setShowSequencing]);

	return (
		<Box>
			{leaseSubtenant && (
				<Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>
					{`This account is a ${subscriptionSubtenantType.toLowerCase()} subtenant of `}
					{isTenantPortal ? (
						parentSubscription.subscriptionName
					) : (
						<Link
							href={getUrlToSubscriptionPage(
								parentSubscription.id,
								facilityId,
								parentSubscription.accountId,
							)}
							target='_blank'
						>
							{parentSubscription.subscriptionName}
						</Link>
					)}
				</Typography>
			)}
			<LeasingRightsTable
				subscriptionId={subscriptionId}
				accountId={accountId}
				setShowDialog={setShowDialog}
				setShowSnackbar={setShowSnackbar}
				getUrlToSubscriptionPage={getUrlToSubscriptionPage}
				readOnly={isTenantPortal}
				compact={showSequencing}
			/>
			{showSequencing && isAdminDashboard && (
				<Sequencing
					subscriptionId={subscriptionId}
					setShowSnackbar={setShowSnackbar}
					setShowDialog={setShowDialog}
				/>
			)}
		</Box>
	);
};
